import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Modal, Form, InputNumber, Avatar, Select, Tooltip } from 'antd';
import { SearchOutlined, PlusOutlined, DollarOutlined, EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import '../Users/Users.css';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  .ant-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const Currencies: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  // Edit functionality moved to detail page
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, pageId, perPage]);

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (search) {
        params.search = search;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_CURRENCIES, {
        params,
      });

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        // Handle both paginate utility format (itemsList) and direct array
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setCurrencies(items);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total:
              paginator.itemCount ||
              paginator.totalCount ||
              paginator.total ||
              0,
          });
        } else {
          setPagination((prev) => ({
            ...prev,
            total: items.length,
          }));
        }
      }
    } catch (error: any) {
      message.error('Failed to fetch assets');
      console.error('Error fetching currencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('pageId', '1'); // Reset to first page on search
    setSearchParams(newParams);
  };

  const handleStatusFilter = (value: string) => {
    const v = value || '';
    setStatusFilter(v);
    const newParams = new URLSearchParams(searchParams);
    if (v) newParams.set('status', v);
    else newParams.delete('status');
    newParams.set('pageId', '1'); // Reset to first page on filter change
    setSearchParams(newParams);
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const handleCreate = () => {
    form.resetFields();
    setModalVisible(true);
  };


  // Edit functionality moved to detail page

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Ensure numeric fields are sent as numbers to match API expectations
      const payload: any = {
        ...values,
        decimalPrecision:
          values.decimalPrecision !== undefined && values.decimalPrecision !== null
            ? Number(values.decimalPrecision)
            : values.decimalPrecision,
        oneUsdValue:
          values.oneUsdValue !== undefined && values.oneUsdValue !== null
            ? Number(values.oneUsdValue)
            : values.oneUsdValue,
        minSweepAmountInUsd:
          values.minSweepAmountInUsd !== undefined && values.minSweepAmountInUsd !== null
            ? Number(values.minSweepAmountInUsd)
            : values.minSweepAmountInUsd,
      };

      // Only create, edit is done on detail page
      await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_CURRENCY, payload);
      message.success('Asset created successfully');

      setModalVisible(false);
      fetchCurrencies();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save currency');
    }
  };

  const columns = [
    {
      title: 'Icon',
      key: 'icon',
      width: 80,
      render: (_: any, record: any) => (
        <Avatar
          src={record.iconUrl}
          icon={<DollarOutlined />}
          size={40}
          style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
        />
      ),
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Decimal Precision',
      dataIndex: 'decimalPrecision',
      key: 'decimalPrecision',
    },
    {
      title: 'Value per 1 USD',
      dataIndex: 'oneUsdValue',
      key: 'oneUsdValue',
      render: (value: number, record: any) => {
        if (!value) return '-';
        return `${Number(value).toFixed(8)} ${record.symbol || ''}`;
      },
    },
    {
      title: 'Exchange Rate (USD)',
      key: 'exchangeRate',
      render: (_: any, record: any) => {
        const oneUsdValue = Number(record.oneUsdValue);
        if (!oneUsdValue || oneUsdValue === 0 || isNaN(oneUsdValue)) return '-';
        const rate = 1 / oneUsdValue;
        // Format based on value magnitude
        if (rate >= 1000) {
          return `$${rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        } else if (rate >= 1) {
          return `$${rate.toFixed(2)}`;
        } else if (rate >= 0.01) {
          return `$${rate.toFixed(4)}`;
        } else {
          return `$${rate.toFixed(8)}`;
        }
      },
    },
    {
      title: 'Currency Status',
      dataIndex: 'currencyStatus',
      key: 'currencyStatus',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Min Sweep Amount',
      dataIndex: 'minSweepAmount',
      key: 'minSweepAmount',
      render: (amount: number, record: any) => amount ? `${Number(amount).toFixed(8)} ${record.symbol || ''}` : '-',
    },
    {
      title: 'Min Sweep Amount (USD)',
      dataIndex: 'minSweepAmountInUsd',
      key: 'minSweepAmountInUsd',
      render: (amount: number) => amount ? `$${Number(amount).toFixed(2)}` : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Allow in Watcher',
      dataIndex: 'allowInOnchainWatcherMonitor',
      key: 'allowInOnchainWatcherMonitor',
      render: (value: boolean) => (
        <Tag color={value !== false ? 'green' : 'red'}>
          {value !== false ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Supports RPC Polling',
      dataIndex: 'supportsRPCPolling',
      key: 'supportsRPCPolling',
      render: (value: boolean) => (
        <Tag color={value !== false ? 'green' : 'red'}>
          {value !== false ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/kivu/currencies/${record.id}`)}
            size="small"
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Assets
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Create Currency
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Search
              </Text>
              <Input
                placeholder="Search by symbol or name"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                onBlur={() => {
                  const newParams = new URLSearchParams(searchParams);
                  if (search) {
                    newParams.set('search', search);
                  } else {
                    newParams.delete('search');
                  }
                  newParams.set('pageId', '1'); // Reset to first page on search
                  setSearchParams(newParams);
                }}
                style={{ maxWidth: 400 }}
              />
            </div>

            <div>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Status
              </Text>
              <Select
                value={statusFilter || undefined}
                onChange={handleStatusFilter}
                allowClear
                placeholder="All statuses"
                style={{ width: 220 }}
                options={[
                  { value: 'ACTIVE', label: 'ACTIVE' },
                  { value: 'SUSPENDED', label: 'SUSPENDED' },
                  { value: 'DELETED', label: 'DELETED' },
                  { value: 'PENDING', label: 'PENDING' },
                ]}
              />
            </div>
          </div>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ marginBottom: 12, color: COLORS.textSecondary }}>
          Total {pagination.total} assets
        </div>
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={currencies}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} assets
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            className="dark-table"
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title="Create Asset"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Create"
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="symbol"
            label="Symbol"
            rules={[{ required: true, message: 'Please enter symbol' }]}
          >
            <Input placeholder="e.g., BTC, ETH" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="e.g., Bitcoin, Ethereum" />
          </Form.Item>
          <Form.Item
            name="decimalPrecision"
            label="Decimal Precision"
            rules={[{ required: true, message: 'Please enter decimal precision' }]}
          >
            <InputNumber min={0} max={18} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="iconUrl"
            label="Icon URL"
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="oneUsdValue"
            label="Value per 1 USD (e.g., 0.000025 means 0.000025 units = 1 USD)"
            rules={[{ required: true, message: 'Please enter value per 1 USD' }]}
          >
            <InputNumber min={0} step={0.00000001} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="minSweepAmountInUsd"
            label="Min Sweep Amount (USD)"
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="aggregatorSlug"
            label={
              <span>
                CoinMarketCap Slug{' '}
                <Tooltip title="The CoinMarketCap slug used to fetch live prices (e.g., 'bitcoin', 'ethereum', 'tether'). Use 'assetChainPricing' for custom pricing logic.">
                  <InfoCircleOutlined style={{ color: '#22D3EE', cursor: 'pointer' }} />
                </Tooltip>
              </span>
            }
          >
            <Input placeholder="e.g., bitcoin, ethereum, tether" />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default Currencies;

